import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelVideoJob, getVideoJob, listVideoJobs } from '../api/generateVideo.api';
import {
  ACTIVE_JOB_STATUSES,
  JOB_PAGE_SIZE,
  JOB_POLL_BACKOFF_FACTOR,
  JOB_POLL_MAX_SECONDS,
} from '../constants';
import type { VideoJob } from '../types';

export function isActiveJob(job: VideoJob): boolean {
  return (ACTIVE_JOB_STATUSES as readonly string[]).includes(job.status);
}

/**
 * Job history with polling for queued/running jobs: each job is polled after the server's
 * Retry-After, backing off to JOB_POLL_MAX_SECONDS, and stops as soon as it finishes.
 */
export function useVideoJobs(options: { minPollSeconds: number; onJobFinished?: () => void }) {
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pollDelays = useRef(new Map<string, number>());
  const onJobFinished = useRef(options.onJobFinished);
  onJobFinished.current = options.onJobFinished;

  const upsert = useCallback((job: VideoJob) => {
    setJobs((current) => {
      const index = current.findIndex((item) => item.jobId === job.jobId);
      if (index === -1) return [job, ...current];
      const next = [...current];
      next[index] = job;
      return next;
    });
  }, []);

  const loadPage = useCallback(async (cursor?: string) => {
    if (cursor) setLoadingMore(true);
    try {
      const page = await listVideoJobs({ cursor, limit: JOB_PAGE_SIZE });
      setJobs((current) => {
        if (!cursor) return page.items;
        const seen = new Set(current.map((job) => job.jobId));
        return [...current, ...page.items.filter((job) => !seen.has(job.jobId))];
      });
      setNextCursor(page.nextCursor);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const minPollSeconds = useRef(options.minPollSeconds);
  minPollSeconds.current = options.minPollSeconds;
  const timers = useRef(new Map<string, number>());
  // Bumped after a failed poll so the effect below re-arms that job.
  const [retryTick, setRetryTick] = useState(0);

  const schedule = useCallback(
    (jobId: string) => {
      const job = jobsRef.current.find((item) => item.jobId === jobId);
      const base = Math.max(job?.retryAfterSeconds ?? 0, minPollSeconds.current, 1);
      const delay = Math.min(pollDelays.current.get(jobId) ?? base, JOB_POLL_MAX_SECONDS);
      pollDelays.current.set(jobId, delay * JOB_POLL_BACKOFF_FACTOR);

      timers.current.set(
        jobId,
        window.setTimeout(async () => {
          timers.current.delete(jobId);
          try {
            const latest = await getVideoJob(jobId);
            upsert(latest);
            if (!isActiveJob(latest)) {
              pollDelays.current.delete(jobId);
              onJobFinished.current?.();
            }
          } catch {
            pollDelays.current.set(jobId, JOB_POLL_MAX_SECONDS);
            setRetryTick((tick) => tick + 1);
          }
        }, delay * 1000)
      );
    },
    [upsert]
  );

  const activeIds = jobs
    .filter(isActiveJob)
    .map((job) => job.jobId)
    .join(',');

  // One timer per active job; a job's own update re-arms only that job.
  useEffect(() => {
    const active = new Set(activeIds ? activeIds.split(',') : []);
    for (const jobId of active) {
      if (!timers.current.has(jobId)) schedule(jobId);
    }
    for (const [jobId, timer] of timers.current) {
      if (!active.has(jobId)) {
        window.clearTimeout(timer);
        timers.current.delete(jobId);
      }
    }
  }, [activeIds, jobs, retryTick, schedule]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const cancel = useCallback(
    async (jobId: string) => {
      upsert(await cancelVideoJob(jobId));
    },
    [upsert]
  );

  return {
    jobs,
    loading,
    loadingMore,
    hasMore: Boolean(nextCursor),
    loadMore: () => (nextCursor ? loadPage(nextCursor) : Promise.resolve()),
    addJob: upsert,
    cancel,
  };
}
